import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { JobImageRunView, JobImageVersionView, JobPostWords, JobPostWordsView, MakeJobImageInput } from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPost, apiPut, errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { jobKey } from './jobs-api';

// ADR 0064/0065 — the job post image: its words (free to prepare and edit), Approve & make image, Update image and
// earlier versions. Under the job's key, so the API's realtime hint (`['jobs']`) refreshes all of it.

export function jobPostWordsKey(jobId: string): readonly unknown[] {
  return [...jobKey(jobId), 'post-words'];
}

export function jobImageRunKey(jobId: string): readonly unknown[] {
  return [...jobKey(jobId), 'image-run'];
}

export function jobImageVersionsKey(jobId: string): readonly unknown[] {
  return [...jobKey(jobId), 'image-versions'];
}

/** Where an earlier image opens. The API checks the job's permission. */
export function jobImageVersionUrl(jobId: string, runId: string): string {
  return `/api/v1/jobs/${jobId}/image-versions/${runId}/file`;
}

export function useJobPostWords(jobId: string): UseQueryResult<JobPostWordsView, ApiError> {
  return useQuery<JobPostWordsView, ApiError>({
    queryKey: jobPostWordsKey(jobId),
    queryFn: () => apiGet<JobPostWordsView>(`/jobs/${jobId}/post-words`),
    placeholderData: (previous) => previous,
  });
}

export function useJobImageRun(jobId: string): UseQueryResult<JobImageRunView | null, ApiError> {
  return useQuery<JobImageRunView | null, ApiError>({
    queryKey: jobImageRunKey(jobId),
    queryFn: () => apiGet<JobImageRunView | null>(`/jobs/${jobId}/image-runs/latest`),
    placeholderData: (previous) => previous,
  });
}

export function useJobImageVersions(jobId: string): UseQueryResult<readonly JobImageVersionView[], ApiError> {
  return useQuery<readonly JobImageVersionView[], ApiError>({
    queryKey: jobImageVersionsKey(jobId),
    queryFn: () => apiGet<readonly JobImageVersionView[]>(`/jobs/${jobId}/image-versions`),
    placeholderData: (previous) => previous,
  });
}

/** Saved as you leave a line (§5a rule 1): the words show at once and roll back with the reason if refused. */
export function useSaveJobPostWords(jobId: string): { readonly save: (words: JobPostWords) => void; readonly reset: () => void } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const key = jobPostWordsKey(jobId);
  const rollback = (before: JobPostWordsView | undefined, message: string, error: ApiError): void => {
    queryClient.setQueryData(key, before);
    show({ tone: 'danger', message, detail: errorMessage(error) });
  };
  const save = useMutation<JobPostWordsView, ApiError, JobPostWords, { readonly before: JobPostWordsView | undefined }>({
    mutationFn: (words) => apiPut<JobPostWordsView>(`/jobs/${jobId}/post-words`, { words }),
    onMutate: async (words) => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<JobPostWordsView>(key);
      if (before) queryClient.setQueryData<JobPostWordsView>(key, { ...before, words, isEdited: true });
      return { before };
    },
    onSuccess: (view) => queryClient.setQueryData(key, view),
    onError: (error, _words, context) => rollback(context?.before, "Couldn't save the post words", error),
  });
  const reset = useMutation<JobPostWordsView, ApiError, void, { readonly before: JobPostWordsView | undefined }>({
    mutationFn: () => apiDelete<JobPostWordsView>(`/jobs/${jobId}/post-words`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<JobPostWordsView>(key);
      if (before) queryClient.setQueryData<JobPostWordsView>(key, { ...before, words: before.fromJob, isEdited: false });
      return { before };
    },
    onSuccess: (view) => queryClient.setQueryData(key, view),
    onError: (error, _input, context) => rollback(context?.before, "Couldn't reset the post words", error),
  });
  return { save: (words) => save.mutate(words), reset: () => reset.mutate() };
}

/** Act first (§5a rule 1): the slot says "making" at once, and rolls back with the reason if the server refuses. */
export function useMakeJobImage(jobId: string): { readonly make: (input: MakeJobImageInput) => void } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const mutation = useMutation<JobImageRunView, ApiError, MakeJobImageInput, { readonly before: JobImageRunView | null | undefined }>({
    mutationFn: (input) => apiPost<JobImageRunView>(`/jobs/${jobId}/image-runs`, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: jobImageRunKey(jobId) });
      const before = queryClient.getQueryData<JobImageRunView | null>(jobImageRunKey(jobId));
      const making: JobImageRunView = { id: 'pending', status: 'making', trigger: input.mode === 'edit' ? 'edited' : 'asked', problem: null, startedAt: new Date().toISOString() };
      queryClient.setQueryData(jobImageRunKey(jobId), making);
      return { before };
    },
    onSuccess: (run) => queryClient.setQueryData(jobImageRunKey(jobId), run),
    onError: (error, _input, context) => {
      queryClient.setQueryData(jobImageRunKey(jobId), context?.before ?? null);
      show({ tone: 'danger', message: "Couldn't make the image", detail: errorMessage(error) });
    },
  });
  return { make: (input) => mutation.mutate(input) };
}

/** Go back to an earlier image: the strip marks it current at once; the job's image follows on the refetch. */
export function useRestoreJobImage(jobId: string): { readonly restore: (runId: string) => void } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const key = jobImageVersionsKey(jobId);
  const mutation = useMutation<readonly JobImageVersionView[], ApiError, string, { readonly before: readonly JobImageVersionView[] | undefined }>({
    mutationFn: (runId) => apiPost<readonly JobImageVersionView[]>(`/jobs/${jobId}/image-versions/${runId}/restore`, {}),
    onMutate: async (runId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<readonly JobImageVersionView[]>(key);
      if (before) queryClient.setQueryData(key, before.map((version) => ({ ...version, isCurrent: version.runId === runId })));
      return { before };
    },
    onSuccess: async (versions) => {
      queryClient.setQueryData(key, versions);
      await queryClient.invalidateQueries({ queryKey: jobKey(jobId), exact: true });
    },
    onError: (error, _runId, context) => {
      queryClient.setQueryData(key, context?.before);
      show({ tone: 'danger', message: "Couldn't go back to that image", detail: errorMessage(error) });
    },
  });
  return { restore: (runId) => mutation.mutate(runId) };
}
